import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { compressImage, drainQueue, enqueueUpload, hashBytes, listQueued } from './upload-queue';

interface Job {
  workOrder: {
    id: string;
    number: string;
    status: string;
    unitLine: string;
    readAndSize: string | null;
    installDateText: string;
    copyDescription: string | null;
    specialInstructions: string | null;
    clientName: string | null;
    materialCarrier: string | null;
    materialTrackingNumber: string | null;
  };
  location: {
    address: string;
    crossStreetOffset: string | null;
    latitude: number | null;
    longitude: number | null;
    accessNotes: string | null;
    requiresCrane: boolean;
    requiresBucket: boolean;
    requiresClimb: boolean;
  } | null;
  items: { id: string; label: string; isRequired: boolean; isCompleted: boolean }[];
  photos: { id: string; category: string; url: string }[];
  invoice: { amountCents: number; invoiceNumber: string | null } | null;
  submission: { ok: boolean; blockers: string[] };
}

/**
 * The installer's whole interface.
 *
 * Built for a phone held at arm's length in direct sun on a highway shoulder:
 * large targets, high contrast, no login, and every destructive-feeling action
 * confirmed. Assumes a bad connection throughout — uploads go to IndexedDB
 * first and drain in the background.
 */
export function Portal({ token }: { token: string }) {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingCategory = useRef<string>('after');

  const load = useCallback(() => {
    api
      .get<Job>(`/api/portal/${token}/job`)
      .then((d) => {
        setJob(d);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  const drain = useCallback(async () => {
    const result = await drainQueue();
    setQueued(result.remaining);
    if (result.uploaded > 0) load();
  }, [load]);

  useEffect(() => {
    load();
    void listQueued().then((q) => setQueued(q.length));
  }, [load]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void drain();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const timer = setInterval(() => {
      if (navigator.onLine) void drain();
    }, 20_000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(timer);
    };
  }, [drain]);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleFiles(files: FileList): Promise<void> {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const { bytes, contentType } = await compressImage(file);
        await enqueueUpload({
          token,
          category: pendingCategory.current,
          contentType,
          bytes,
          sha256: await hashBytes(bytes),
          capturedAt: file.lastModified || Date.now(),
        });
      }
      setQueued((await listQueued()).length);
      await drain();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (error !== null && job === null) {
    return (
      <main className="mx-auto max-w-lg p-6 text-center">
        <Logo />
        <p className="mt-6 border border-red-300 bg-red-50 p-4 text-base text-red-800">{error}</p>
      </main>
    );
  }

  if (job === null) return <main className="p-6 text-center text-base">Loading…</main>;

  const { workOrder: wo, location, items, photos, submission } = job;
  const done = items.filter((i) => i.isCompleted).length;
  const mapHref =
    location?.latitude != null && location.longitude != null
      ? `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`
      : location
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`
        : null;

  return (
    <main className="mx-auto max-w-lg pb-24">
      <header className="sticky top-0 z-10 border-b-2 border-black bg-white px-4 py-3">
        <Logo />
        <h1 className="mt-1 text-lg font-bold">{wo.number}</h1>
        <p className="text-sm text-neutral-700">{wo.unitLine}</p>
        {!online && (
          <p className="mt-1 bg-amber-100 px-2 py-1 text-sm font-medium text-amber-900">
            Offline — your work is saved on this phone and will send itself.
          </p>
        )}
        {queued > 0 && (
          <p className="mt-1 text-sm text-neutral-600">{queued} photo(s) waiting to upload…</p>
        )}
      </header>

      {error !== null && (
        <p role="alert" className="mx-4 mt-3 border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </p>
      )}

      <div className="space-y-4 p-4">
        {wo.status === 'dispatched' && (
          <button
            onClick={() => void act(() => api.post(`/api/portal/${token}/acknowledge`))}
            disabled={busy}
            className="w-full bg-harkey-gold py-4 text-lg font-bold text-black disabled:opacity-50"
          >
            Confirm you got this job
          </button>
        )}

        <section className="border border-neutral-300">
          <h2 className="border-b border-neutral-300 bg-neutral-100 px-3 py-2 text-sm font-bold uppercase">
            The job
          </h2>
          <dl className="p-3 text-base">
            {wo.readAndSize !== null && <Row label="Read / size">{wo.readAndSize}</Row>}
            <Row label="Install date">{wo.installDateText}</Row>
            {location?.crossStreetOffset != null && <Row label="Offset">{location.crossStreetOffset}</Row>}
            {wo.copyDescription !== null && <Row label="Copy">{wo.copyDescription}</Row>}
            {wo.specialInstructions !== null && <Row label="Instructions">{wo.specialInstructions}</Row>}
            {wo.materialTrackingNumber !== null && (
              <Row label="Material">
                {[wo.materialCarrier, wo.materialTrackingNumber].filter(Boolean).join(' ')}
              </Row>
            )}
            {location !== null && (location.requiresCrane || location.requiresBucket || location.requiresClimb) && (
              <Row label="Access">
                {[
                  location.requiresCrane && 'crane',
                  location.requiresBucket && 'bucket',
                  location.requiresClimb && 'high climb',
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Row>
            )}
            {location?.accessNotes != null && <Row label="Notes">{location.accessNotes}</Row>}
          </dl>
          {mapHref !== null && (
            <a
              href={mapHref}
              target="_blank"
              rel="noreferrer"
              className="block border-t border-neutral-300 bg-neutral-50 py-3 text-center text-base font-semibold text-blue-800"
            >
              Open in Maps
            </a>
          )}
        </section>

        <section className="border border-neutral-300">
          <h2 className="border-b border-neutral-300 bg-neutral-100 px-3 py-2 text-sm font-bold uppercase">
            Checklist ({done}/{items.length})
          </h2>
          <ul>
            {items.map((item) => (
              <li key={item.id} className="border-b border-neutral-200 last:border-0">
                <label className="flex min-h-14 cursor-pointer items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={item.isCompleted}
                    disabled={busy}
                    onChange={(e) =>
                      void act(() =>
                        api.patch(`/api/portal/${token}/items/${item.id}`, {
                          isCompleted: e.target.checked,
                        }),
                      )
                    }
                    className="h-7 w-7 shrink-0 accent-black"
                  />
                  <span className={`text-base ${item.isCompleted ? 'text-neutral-400 line-through' : ''}`}>
                    {item.label}
                    {item.isRequired && <span className="text-red-600"> *</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>

        <section className="border border-neutral-300">
          <h2 className="border-b border-neutral-300 bg-neutral-100 px-3 py-2 text-sm font-bold uppercase">
            Photos ({photos.length})
          </h2>
          <div className="grid grid-cols-2 gap-2 p-3">
            {(['before', 'after', 'damage'] as const).map((category) => (
              <button
                key={category}
                disabled={busy}
                onClick={() => {
                  pendingCategory.current = category;
                  fileInput.current?.click();
                }}
                className="border-2 border-black bg-white py-4 text-base font-bold uppercase disabled:opacity-50"
              >
                + {category}
              </button>
            ))}
            <button
              disabled={busy}
              onClick={() => {
                pendingCategory.current = 'during';
                fileInput.current?.click();
              }}
              className="border-2 border-black bg-white py-4 text-base font-bold uppercase disabled:opacity-50"
            >
              + during
            </button>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-1 p-3 pt-0">
              {photos.map((p) => (
                <figure key={p.id}>
                  <img
                    src={`${p.url}?t=${encodeURIComponent(token)}`}
                    alt={p.category}
                    loading="lazy"
                    className="aspect-square w-full border border-neutral-300 object-cover"
                  />
                  <figcaption className="text-center text-xs uppercase text-neutral-600">
                    {p.category}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>

        <InvoiceSection token={token} invoice={job.invoice} onSaved={load} />

        <section className="border-2 border-black">
          <h2 className="border-b-2 border-black bg-neutral-100 px-3 py-2 text-sm font-bold uppercase">
            Submit
          </h2>
          <div className="p-3">
            {!submission.ok && (
              <ul className="mb-3 space-y-1">
                {submission.blockers.map((b) => (
                  <li key={b} className="text-sm text-red-700">
                    • {b}
                  </li>
                ))}
              </ul>
            )}
            <textarea
              id="installer-note"
              placeholder="Anything we should know? (e.g. frame rusted at bottom left)"
              rows={3}
              className="mb-3 w-full border border-neutral-400 p-2 text-base"
            />
            <button
              disabled={busy || !submission.ok || queued > 0}
              onClick={() => {
                const note = (document.getElementById('installer-note') as HTMLTextAreaElement | null)?.value;
                if (!window.confirm('Submit this job to Harkey Media?')) return;
                void act(() => api.post(`/api/portal/${token}/submit`, { note }));
              }}
              className="w-full bg-harkey-gold py-4 text-lg font-bold text-black disabled:bg-neutral-200 disabled:text-neutral-500"
            >
              {queued > 0 ? 'Waiting for photos to upload…' : 'Submit job'}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 border-b border-neutral-100 py-2 last:border-0">
      <dt className="w-28 shrink-0 text-sm text-neutral-500">{label}</dt>
      <dd className="flex-1">{children}</dd>
    </div>
  );
}

function InvoiceSection({
  token,
  invoice,
  onSaved,
}: {
  token: string;
  invoice: { amountCents: number; invoiceNumber: string | null } | null;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState(invoice ? String(invoice.amountCents / 100) : '');
  const [number, setNumber] = useState(invoice?.invoiceNumber ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <section className="border border-neutral-300">
      <h2 className="border-b border-neutral-300 bg-neutral-100 px-3 py-2 text-sm font-bold uppercase">
        Your invoice
      </h2>
      <div className="space-y-2 p-3">
        <label className="block text-sm text-neutral-600">
          Amount (USD)
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setSaved(false);
            }}
            className="mt-1 w-full border border-neutral-400 p-2 text-base"
          />
        </label>
        <label className="block text-sm text-neutral-600">
          Invoice number
          <input
            value={number}
            onChange={(e) => {
              setNumber(e.target.value);
              setSaved(false);
            }}
            className="mt-1 w-full border border-neutral-400 p-2 text-base"
          />
        </label>
        <button
          disabled={saving || amount === ''}
          onClick={() => {
            setSaving(true);
            api
              .put(`/api/portal/${token}/invoice`, {
                amountCents: Math.round(Number(amount) * 100),
                invoiceNumber: number === '' ? null : number,
              })
              .then(() => {
                setSaved(true);
                onSaved();
              })
              .finally(() => setSaving(false));
          }}
          className="w-full border-2 border-black bg-white py-3 text-base font-bold disabled:opacity-40"
        >
          {saved ? 'Saved ✓' : 'Save invoice'}
        </button>
      </div>
    </section>
  );
}

function Logo() {
  return (
    <span className="inline-block bg-black px-2 py-1 text-base font-bold tracking-tight text-white">
      Harkey<span className="text-harkey-gold">Media</span>
    </span>
  );
}
